import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from 'core/services/auth.service';
import { LevelsWidgetComponent } from './levels-widget/levels-widget.component';
import { KorviuListComponent } from 'shared/components/korviu-list/korviu-list.component';
import { UsersService } from 'core';
import { IPagination } from 'shared/types/pagination.interface';
import { IFilter } from 'shared/types/filter.interface';
import { ISorting } from 'shared/types/sorting.interface';
import { map, Observable } from 'rxjs';
import { IKorviuListConfig, KorviuListItemTypes } from 'shared/types/korviu-list.config';
import { User } from 'shared/types/user.model';
import { IUser } from 'shared/types/user.interface';
import { IGetPaginatedResponse } from 'shared/types/get-paginated-response.interface';
import { Router } from '@angular/router';
import { SortOrders } from 'shared/types/sort-order.enum';
import { ListFiltersTemplateDirective } from 'shared/directives/list-filters.directive';
import { NavigationService } from 'core/services/navigation.service';

@Component({
  selector: 'app-my-forest-main-info',
  imports: [TranslatePipe, LevelsWidgetComponent, KorviuListComponent, ListFiltersTemplateDirective],
  templateUrl: './my-forest-main-info.component.html',
  styleUrl: './my-forest-main-info.component.scss'
})
export class MyForestMainInfoComponent {
  readonly myForestList = viewChild<KorviuListComponent>('myForestList');

  readonly authService = inject(AuthService);
  readonly usersService = inject(UsersService);
  readonly router = inject(Router);
  readonly navigationService = inject(NavigationService);

  private readonly quickFilter = signal<'leaves' | 'referrals' | null>(null);
  readonly leavesSelected = computed(() => this.quickFilter() === 'leaves');
  readonly myReferralsSelected = computed(() => this.quickFilter() === 'referrals');

  readonly communityNumber = computed<number>(() => this.authService.currentUser$()?.totalNetworkSize ?? 0);
  readonly ganarbolsNumber = computed<number>(() => this.authService.currentUser?.totalGanarbols ?? 0);
  readonly ganacuatesNumber = signal<number>(0);
  readonly myGanarbolsCount = signal<number>(0);

  readonly communityListConfig: IKorviuListConfig = {
    getFn: this.getMyForest.bind(this),
    pagination: { page: 1, pageSize: 12 },
    sortSections: [
      {
        localizableName: 'my-forest.name',
        options: [
          {
            localizableName: 'common.sortOption.A-Z',
            sortDirection: { sortBy: 'full_name', sortOrder: SortOrders.ASCEND }
          },
          {
            localizableName: 'common.sortOption.Z-A',
            sortDirection: { sortBy: 'full_name', sortOrder: SortOrders.DESCEND }
          }
        ]
      },
      {
        localizableName: 'my-forest.ganarbolsAmount',
        options: [
          {
            localizableName: 'common.sortOption.highest',
            sortDirection: { sortBy: 'total_gana_arboles', sortOrder: SortOrders.DESCEND }
          },
          {
            localizableName: 'common.sortOption.lowest',
            sortDirection: { sortBy: 'total_gana_arboles', sortOrder: SortOrders.ASCEND }
          }
        ]
      },
      {
        localizableName: 'my-forest.referrals',
        options: [
          {
            localizableName: 'common.sortOption.highest',
            sortDirection: { sortBy: 'total_referrals', sortOrder: SortOrders.DESCEND }
          },
          {
            localizableName: 'common.sortOption.lowest',
            sortDirection: { sortBy: 'total_referrals', sortOrder: SortOrders.ASCEND }
          }
        ]
      },
      {
        localizableName: 'my-forest.contribution',
        options: [
          {
            localizableName: 'common.sortOption.highest',
            sortDirection: { sortBy: 'contribution', sortOrder: SortOrders.DESCEND }
          },
          {
            localizableName: 'common.sortOption.lowest',
            sortDirection: { sortBy: 'contribution', sortOrder: SortOrders.ASCEND }
          }
        ]
      }
    ],
    filterSections: [
      {
        name: '',
        localizableName: 'my-forest.userType',
        parameter_name: 'user_type',
        selectionMode: 'single',
        checkboxModel: [
          {
            label: '',
            localizableName: 'my-forest.ganacuate',
            value: 'ganacuate',
            parameter_name: 'ganacuate'
          },
          {
            label: '',
            localizableName: 'my-forest.ganarbol',
            value: 'ganarbol',
            parameter_name: 'ganarbol'
          }
        ]
      }
    ],
    itemType: KorviuListItemTypes.MY_FOREST,
    detailsFn: this.goToUser.bind(this)
  };

  constructor() {
    this.loadDirectReferralsCount();
  }

  onLeavesClick(): void {
    this.quickFilter.set(this.leavesSelected() ? null : 'leaves');
    this.myForestList().reloadData(true);
  }

  onRemoveLeavesClick(event: MouseEvent): void {
    event.stopPropagation();
    this.quickFilter.set(null);
    this.myForestList().reloadData(true);
  }

  onMyReferralsClick(): void {
    this.quickFilter.set(this.myReferralsSelected() ? null : 'referrals');
    this.myForestList().reloadData(true);
  }

  onRemoveMyReferralsClick(event: MouseEvent): void {
    event.stopPropagation();
    this.quickFilter.set(null);
    this.myForestList().reloadData(true);
  }

  getMyForest(
    pagination: IPagination,
    filters: IFilter[],
    sorting: ISorting
  ): Observable<IGetPaginatedResponse<User[]>> {
    filters = [...filters];

    const quickFilter = this.quickFilter();
    if (quickFilter) {
      filters.push({ value: quickFilter, name: 'filter_type' });
    }

    return this.usersService.getMyForest<IUser[]>(pagination, filters, sorting).pipe(
      map(response => ({
        ...response,
        results: response.results.map(r => new User(r))
      }))
    );
  }

  goToUser(user: User): void {
    void this.router.navigate(['/workspace/my-forest/user/', user.publicId || user.id]);
  }

  onGrowYourCommunityClick(): void {
    this.navigationService.setNextGoBackUrl('/workspace/my-forest');
    void this.router.navigate(['/workspace/my-referrals']);
  }

  private loadDirectReferralsCount(): void {
    this.usersService.getDirectReferralsCount().subscribe(response => {
      this.ganacuatesNumber.set(response.counts?.my_ganacuates_count ?? 0);
      this.myGanarbolsCount.set(response.counts?.my_ganarbols_count ?? 0);
    });
  }
}
